import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { pathgen } from 'object-path-generator';
import { Box, Button, Spinner, Typography, useOverlayContext } from 'seagull';
import { redirect, routes } from '~/constants';
import { useUtmsParams } from '~/hooks/useUtmsParams';
import { useLocaleKeys } from '~/i18n';
import { handleRequest } from '~/infra/utils';
import XeroLogo from '~/integrations/assets/XeroLogo';
import { LoginMethod, useLoginTelemetry } from '~/loginTelemetry/useLoginTelemetry';
import { useOAuthSignIn } from '~/oauth/useOAuthSignIn';
import { useStoreActions } from '~/store/store-context';
import styles from './XeroSignInButton.module.scss';
export const XeroSignInButtonTestIds = pathgen('XeroSignInButtonTestIds');
export function XeroSignInButton(props) {
    const { mode, ensureEmail, width, returnTo: returnToProp } = props;
    const getUtms = useUtmsParams();
    const { api } = useStoreActions();
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: xeroTexts } = useLocaleKeys('auth.screens.xero');
    const { pathname, search } = useLocation();
    const { showErrorToast } = useOverlayContext();
    const { authRequestParams, webOrigin, signIn: oauthSignIn } = useOAuthSignIn('xero', mode);
    const [loading, setLoading] = useState(false);
    const { loginAttempted, loginFailed } = useLoginTelemetry();
    const searchReturnTo = useMemo(()=>redirect.getReturnToFromSearch(search), [
        search
    ]);
    const returnTo = returnToProp !== null && returnToProp !== void 0 ? returnToProp : searchReturnTo;
    const iconSize = 24;
    async function handleClick() {
        setLoading(true);
        const utms = getUtms();
        const redirectUrl = returnTo ? "".concat(webOrigin).concat(redirect.getPathAndSearch({
            returnTo
        })) : "".concat(webOrigin).concat(routes.home);
        const request = mode === 'signUp' ? api.auth.xeroSignUp(_object_spread({
            redirectUrl,
            utms
        }, authRequestParams)) : api.auth.xeroSignIn(_object_spread({
            redirectUrl,
            utms,
            ensureEmail: ensureEmail ? {
                email: ensureEmail,
                errorUrl: "".concat(webOrigin).concat(pathname).concat(routes.loginError)
            } : undefined
        }, authRequestParams));
        const { success, data } = await handleRequest(request);
        const showXeroError = ()=>showErrorToast(commonTexts.toasts.failedToContinueWithXero(), XeroSignInButtonTestIds.ErrorToast());
        if (!success || !data) {
            setLoading(false);
            loginFailed(LoginMethod.Xero);
            showXeroError();
            return;
        }
        const outcome = await loginAttempted(LoginMethod.Xero, ()=>oauthSignIn(data));
        setLoading(outcome === 'redirecting');
    }
    return /*#__PURE__*/ _jsx(Box, {
        className: styles.root,
        children: /*#__PURE__*/ _jsx(Button, {
            testId: XeroSignInButtonTestIds.Button(),
            bi: {
                name: 'xero_signin'
            },
            className: styles.button,
            skin: "primaryOutline",
            disabled: loading,
            onClick: handleClick,
            prefixIcon: loading ? /*#__PURE__*/ _jsx(Spinner, {
                size: iconSize,
                testId: XeroSignInButtonTestIds.Loading()
            }) : /*#__PURE__*/ _jsx(XeroLogo, {
                height: iconSize
            }),
            style: width != null ? {
                width
            } : undefined,
            children: /*#__PURE__*/ _jsx(Typography, {
                skin: loading ? 'disabled' : 'standard',
                children: xeroTexts.continueWithXero()
            })
        })
    });
}
